import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import { MediaFile, useMediaLibContext } from "../medialib";
import { Attachment, NotificationTask } from "./useNotificationTasks";
import { CalendarEvent, createIcal, useCalendarEventCreate } from "./CalendarEvents";

interface Config {
    task: Partial<NotificationTask> | null;
    update: (changes: Partial<NotificationTask>) => void;
}

export const useAttachments = ({ task, update: updateTask }: Config) => {
    const medialib = useMediaLibContext();
    const { enqueueSnackbar } = useSnackbar();
    const { formatMessage } = useIntl();

    const attachments = (task?.attachments || []);

    const add = (mf: MediaFile, filename?: string) => {
        if(!mf.fs_path) {
            enqueueSnackbar(
                formatMessage({ id: "notifications.task.controls.fs_path_not_configured" }),
                { variant: "error", autoHideDuration: 5000 }
                );
            return;
        }
        const attachment = { filename: filename || mf.filename, filepath: mf.fs_path };
        updateTask({ attachments: [...attachments, attachment ]})
    }

    const remove = (idx: number) => {
        updateTask({ attachments: attachments.filter((_,i) => i !== idx) })
    }

    const update = (idx: number, changes: Partial<Attachment>) => {
        updateTask({ attachments: attachments.map((a,i) => i === idx ? { ...a, ...changes } : a) })
    }

    const saveAndAttachICal = (x: CalendarEvent) => {
      const text = createIcal(x).toString();
      const data = new Blob([text], {type: 'text/plain'});
      const file = new File([data], `${x.summary}.ics`);
      return medialib.uploadFile(file)
        .then(mf => add(mf, "invite.ics"));
    }

    const calendarEvent = useCalendarEventCreate(saveAndAttachICal);

    return {
        isAllowed: !!medialib.isAvailable,
        attachments,
        add,
        remove,
        update,
        calendarEvent,
    }
}

export type AttachmentsData = ReturnType<typeof useAttachments>;
